import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import {
  getCountryListWeb,
  getNextUserOpenChallengeRoute,
  isKYCApp,
  LocalizePropType,
  selectBillingInfo,
  selectBrandSettings,
  selectUser,
  setBillingInfo,
  updateUser,
  withTextLocalizer,
} from 'getaway-data-layer';
import UpdateAddressView from './UpdateAddressView';
import {
  GA_TITLES,
  isDashboardScreen,
  navigateTo,
  reportGAEvent,
  showAlert,
} from '../../../../utils';
import { AddressModal, AddressSchema } from '../../../../validation';
import AddressAutoCompleteModal from './AddressSearchModal';
import { useQuery } from '../../../../hooks/useQuery';

interface Values {
  streetName: string;
  streetNumber: string;
  postalCode: string;
  city: string;
  country: string;
  companyName?: string;
}

type Props = {
  localize: LocalizePropType;
};

const UpdateAddressContainer = ({ localize }: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const user: any = useSelector(selectUser);
  const brandSettings = useSelector(selectBrandSettings);
  const query = useQuery();
  const isBillingAddress = isKYCApp() && query.get('type') === 'business';
  const billingInfo = useSelector(selectBillingInfo);

  const [country, setCountry] = useState(
    user?.address?.country ||
      brandSettings?.defaultCountryCode?.toUpperCase() ||
      'DE'
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const supportedCountries = useMemo(
    () =>
      getCountryListWeb(localize, brandSettings?.allowedUserAddressCountries),
    []
  );

  const onCountryChange = (value: any) => {
    setCountry(value);
  };

  const onSuccess = (updatedUser) => {
    if (isKYCApp()) {
      history.goBack();
    } else {
      navigateTo(history, getNextUserOpenChallengeRoute(updatedUser));
    }
    reportGAEvent(GA_TITLES.ON_BOARDING.ADDRESS_SUBMITTED);
  };

  const onSubmit = (values: Values) => {
    if (isBillingAddress) {
      dispatch(
        setBillingInfo({
          country: values?.country.trim(),
          city: values?.city.trim(),
          streetName: values?.streetName.trim(),
          streetNumber: values?.streetNumber.trim(),
          postalCode: values?.postalCode.trim(),
          company: values?.companyName,
        })
      );
      onSuccess(user);
    } else {
      dispatch(
        updateUser(
          {
            address: {
              streetName: values?.streetName.trim(),
              streetNumber: values?.streetNumber.trim(),
              city: values?.city.trim(),
              postalCode: values?.postalCode.trim(),
              country: values?.country.trim(),
            },
          },
          onSuccess
        )
      );
    }
  };

  const onClose = () => {
    if (isDashboardScreen()) {
      history.goBack();
    } else {
      showAlert(
        '',
        localize('cancel.FirstSteps.dialog.message'),
        localize('cancel.FirstSteps.dialog.yes'),
        () => navigateTo(history, '/settings'),
        localize('cancel.FirstSteps.dialog.no'),
        () => {},
        true
      );
    }
  };

  const AddressFormik = useFormik({
    initialValues: AddressModal(billingInfo, user, isBillingAddress, country),
    validationSchema: AddressSchema(localize, country, isBillingAddress),
    onSubmit,
  });

  const onCloseModal = () => {
    setIsModalOpen(false);
  };

  const onLocationSelected = (location: any) => {
    const updatedValues = {
      ...AddressFormik.values,
      ...location,
    };

    AddressFormik.setValues(updatedValues);

    setIsModalOpen(false);
  };

  const onSearchAddressClicked = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <AddressAutoCompleteModal
        countryRestriction={AddressFormik.values.country}
        isModalOpen={isModalOpen}
        onCloseModal={onCloseModal}
        onLocationSelected={onLocationSelected}
      />
      <UpdateAddressView
        localize={localize}
        onClose={onClose}
        formik={AddressFormik}
        supportedCountries={supportedCountries}
        onCountryChange={onCountryChange}
        onSearchAddressClicked={onSearchAddressClicked}
        onLocationSelected={onLocationSelected}
        isBillingAddress={isBillingAddress}
      />
    </>
  );
};

export default withTextLocalizer(UpdateAddressContainer);
